/**
 * MeetingsScreen — entry point de la app.
 *
 * Lista los procesamientos del usuario (vía GET /api/processings) y permite:
 *   - Crear un meeting nuevo (subiendo solo el WAV) → "+ Nueva reunión".
 *   - Abrir un meeting existente clickeando su card.
 *   - Borrar un meeting con confirmación inline (Fase 13.B).
 *   - Ver el SRT etiquetado inline si existe en el bucket (Fase 16).
 *   - Re-descargar el SRT etiquetado al disco si existe en el bucket (Fase 15/16).
 *   - Navegar al admin de directorio y glosario (Fase 32).
 *
 * Borrado (Fase 13.B):
 *   Cada card tiene un botón "Borrar" en la esquina. Al hacer click muestra
 *   "¿Borrar? [Confirmar] [Cancelar]" inline reemplazando el botón. Si el
 *   usuario confirma, llama DELETE /api/processings/:id y recarga la lista.
 *   El click en "Borrar" no propaga al card (no abre el meeting).
 *
 * Ver / Re-descarga (Fase 15 / 16):
 *   Cuando un meeting tiene `tagged.srt` en `files`, mostramos dos botones:
 *   - "👁 Ver": navega a ViewerScreen para leer el SRT inline.
 *   - "↓ Descargar": pide una PAR al Worker, descarga desde OCI y dispara
 *     la descarga local con filename `<id>-tagged.srt`.
 *   Errores de descarga van al banner global, mismo patrón que `deleteError`.
 *
 * Admin (Fase 32):
 *   Botón "⚙ Admin" en el header navega a `AdminScreen` (tabs Directorio /
 *   Glosario). Sin protección extra — Cloudflare Access ya garantiza que
 *   solo usuarios autorizados llegan a esta pantalla. No resetea el estado
 *   del meeting porque AdminScreen no toca los atoms del meeting (mismo
 *   patrón que ViewerScreen).
 */

import { useEffect, useRef, useState } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import styled from "styled-components";
import {
  fetchProcessingsAtom,
  processingsErrorAtom,
  processingsListAtom,
  processingsLoadingAtom,
  resetMeetingDataAtom,
  structureDocAtom,
  structureErrorAtom,
  structureLoadingAtom,
} from "../../state/atoms";
import {
  deleteProcessing,
  runPipelineCorrect,
  runPipelineRun,
  runPipelineAnalyze,
  runPipelineAnalyzeStep,
  runPipelineReport,
  runPipelineDrafts,
  getProcessing,
  ApiError,
  fetchTextFromPar,
  getProcessingFile,
} from "../../platform/api";
import type { ProcessingSummary } from "../../platform/api";
import { downloadTextAsFile } from "../../platform/browser-fs";
import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { Stack } from "../components/Stack";
import { useNavigateTo } from "../routing";

export function MeetingsScreen() {
  const list = useAtomValue(processingsListAtom);
  const loading = useAtomValue(processingsLoadingAtom);
  const error = useAtomValue(processingsErrorAtom);
  const fetchProcessings = useSetAtom(fetchProcessingsAtom);
  const navigateTo = useNavigateTo();
  const resetMeetingData = useSetAtom(resetMeetingDataAtom);

  // id del card que está esperando confirmación de borrado.
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  // id del card cuyo borrado está en progreso (llamada en vuelo).
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // Mensaje de error específico de un borrado fallido.
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Fase 15/16 — id del card cuyo re-download está en progreso.
  const [redownloadingId, setRedownloadingId] = useState<string | null>(null);
  // Fase 15/16 — Mensaje de error específico de un re-download fallido.
  const [redownloadError, setRedownloadError] = useState<string | null>(null);

  // Fase 38 — Pipeline IA: id del meeting cuyo pipeline está corriendo.
  const [pipelineRunningId, setPipelineRunningId] = useState<string | null>(null);
  const [pipelineError, setPipelineError] = useState<string | null>(null);
  const pipelinePollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Fase 49 — Análisis estructural P3.
  const [analyzeLoadingId, setAnalyzeLoadingId] = useState<string | null>(null);
  const [reportLoadingId, setReportLoadingId] = useState<string | null>(null);
  const [draftsLoadingId, setDraftsLoadingId] = useState<string | null>(null);
  const setStructureDoc = useSetAtom(structureDocAtom);
  const setStructureLoading = useSetAtom(structureLoadingAtom);
  const setStructureError = useSetAtom(structureErrorAtom);

  useEffect(() => {
    void fetchProcessings();
  }, [fetchProcessings]);

  const handleNewMeeting = () => {
    resetMeetingData();
    navigateTo.newMeeting();
  };

  // Fase 32 — Navega al admin de directorio y glosario.
  // No resetea el estado del meeting (AdminScreen no toca esos atoms,
  // mismo patrón que ViewerScreen). Sin protección extra: Access ya
  // garantiza que solo usuarios autorizados están en esta pantalla.
  const handleAdmin = () => {
    navigateTo.admin();
  };

  const handleOpenMeeting = (proc: ProcessingSummary) => {
    if (
      confirmingId === proc.id ||
      deletingId === proc.id ||
      redownloadingId === proc.id ||
      pipelineRunningId === proc.id
    ) {
      return;
    }
    resetMeetingData();
    navigateTo.resumeMeeting(proc.id);
  };

  const handleDeleteClick = (e: React.MouseEvent, id: string) => {
    e.stopPropagation(); // Evitar que el click abra el meeting.
    setDeleteError(null);
    setConfirmingId(id);
  };

  const handleDeleteCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmingId(null);
  };

  const handleDeleteConfirm = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setConfirmingId(null);
    setDeletingId(id);
    setDeleteError(null);
    try {
      await deleteProcessing(id);
      void fetchProcessings();
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : "No se pudo borrar el procesamiento. Intentá de nuevo.";
      setDeleteError(msg);
    } finally {
      setDeletingId(null);
    }
  };

  // Fase 16 — Abre el visor inline del tagged.srt.
  // No resetea el estado del meeting (ViewerScreen solo usa currentProcessingIdAtom).
  const handleViewTaggedSrt = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    navigateTo.viewer(id, "tagged.srt");
  };

  // Fase 43 — Abre el visor inline del srt-confirmed.srt.
  const handleViewConfirmedSrt = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    navigateTo.viewer(id, "srt-confirmed.srt");
  };

  // Fase 15/16 — Re-descarga del tagged.srt desde el bucket al disco.
  // Flujo: pedir PAR al Worker → fetch directo a OCI → descargar al disco.
  const handleRedownload = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setRedownloadingId(id);
    setRedownloadError(null);
    try {
      const { downloadUrl } = await getProcessingFile(id, "tagged.srt");
      const text = await fetchTextFromPar(downloadUrl);
      // Filename predictible y único por meeting. El id sigue la convención
      // `YYYY-MM-DD_<slug>` así que el archivo es identificable.
      downloadTextAsFile(`${id}-tagged.srt`, text);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : "No se pudo descargar el archivo. Intentá de nuevo.";
      setRedownloadError(msg);
    } finally {
      setRedownloadingId(null);
    }
  };

  // Fase 38 — Navega a CorrectionsScreen para validar las correcciones de P1.
  const handleOpenCorrections = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    navigateTo.corrections(id);
  };

  // Fase 39 — Encadena llamadas a /run para cada paso del pipeline.
  const chainNextStep = async (id: string) => {
    try {
      const resp = await runPipelineRun(id);
      // Si el paso completó y el pipeline sigue en "correcting",
      // encadenar el siguiente paso inmediatamente.
      if (
        resp.status === "correcting" &&
        resp.nextStep
      ) {
        // Llamada recursiva asíncrona — no await para no bloquear.
        void chainNextStep(id);
      }
      // Si status es "corrected", el polling lo detectará.
    } catch (err) {
      // Error en este paso — el polling detectará pipelineError en meta.
      const msg =
        err instanceof ApiError ? err.message : "Error en el pipeline.";
      setPipelineError(msg);
      setPipelineRunningId(null);
      if (pipelinePollRef.current) {
        clearInterval(pipelinePollRef.current);
        pipelinePollRef.current = null;
      }
    }
  };

  // Fase 39 — Polling: revisa pipelineStatus cada 10s. Si está en
  // "correcting" y el chain no está corriendo, dispara /run.
  const startPolling = (id: string) => {
    if (pipelinePollRef.current) {
      clearInterval(pipelinePollRef.current);
    }

    pipelinePollRef.current = setInterval(async () => {
      try {
        const meta = await getProcessing(id);
        if (meta.pipelineStatus === "corrected") {
          if (pipelinePollRef.current) {
            clearInterval(pipelinePollRef.current);
            pipelinePollRef.current = null;
          }
          setPipelineRunningId(null);
          void fetchProcessings();
          return;
        }
        if (meta.pipelineError) {
          if (pipelinePollRef.current) {
            clearInterval(pipelinePollRef.current);
            pipelinePollRef.current = null;
          }
          setPipelineRunningId(null);
          setPipelineError(
            `Error del pipeline: ${meta.pipelineError.message}`,
          );
          return;
        }
        if (!meta.pipelineStatus || meta.pipelineStatus === "none") {
          if (pipelinePollRef.current) {
            clearInterval(pipelinePollRef.current);
            pipelinePollRef.current = null;
          }
          setPipelineRunningId(null);
          setPipelineError("El pipeline no está corriendo. Reintentá iniciarlo.");
        }
      } catch {
        // Error de red — ignorar.
      }
    }, 10_000);
  };

  // Fase 39 — Dispara P1: /correct (202) + encadena /run para cada paso.
  const handleRunPipeline = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();

    setPipelineRunningId(id);
    setPipelineError(null);

    try {
      await runPipelineCorrect(id);
    } catch (err) {
      if (
        err instanceof ApiError &&
        err.status === 409 &&
        (err.body as { currentPipelineStatus?: string } | null)
          ?.currentPipelineStatus === "correcting"
      ) {
        // Ya está en "correcting". Reanudar chain + polling.
        startPolling(id);
        void chainNextStep(id);
        return;
      }
      const msg =
        err instanceof ApiError
          ? err.message
          : "No se pudo iniciar el pipeline.";
      setPipelineError(msg);
      setPipelineRunningId(null);
      return;
    }

    // Iniciar polling y chain de pasos.
    startPolling(id);
    void chainNextStep(id);
  };

  // Fase 49 — Step-by-step: encadena llamadas a /analyze/run.
  const analyzeChainActive = useRef(false);

  const chainAnalyzeStep = async (id: string) => {
    analyzeChainActive.current = true;
    try {
      const resp = await runPipelineAnalyzeStep(id);
      if (
        resp.status === "analyzing" &&
        resp.nextStep
      ) {
        void chainAnalyzeStep(id);
      } else {
        analyzeChainActive.current = false;
      }
    } catch (err) {
      analyzeChainActive.current = false;
      // Si el chain falla, el polling lo reanudará.
      console.warn("chainAnalyzeStep: request failed, polling will retry", err);
    }
  };

  // Fase 49 — Polling: revisa pipelineStatus cada 10s. Si llega a "analyzed", navega.
  // Si currentStep no es null y el chain no está activo, lo reanuda (failsafe).
  const analyzePollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startAnalyzePolling = (id: string) => {
    if (analyzePollRef.current) {
      clearInterval(analyzePollRef.current);
    }

    analyzePollRef.current = setInterval(async () => {
      try {
        const meta = await getProcessing(id);
        if (meta.pipelineStatus === "analyzed") {
          if (analyzePollRef.current) {
            clearInterval(analyzePollRef.current);
            analyzePollRef.current = null;
          }
          setAnalyzeLoadingId(null);
          void fetchProcessings();
          navigateTo.structure(id);
          return;
        }
        if (meta.pipelineError) {
          if (analyzePollRef.current) {
            clearInterval(analyzePollRef.current);
            analyzePollRef.current = null;
          }
          setAnalyzeLoadingId(null);
          setPipelineError(
            `Error del análisis: ${meta.pipelineError.message}`,
          );
          return;
        }
        // Failsafe: si el chain se interrumpió (timeout, error de red),
        // el polling lo reanuda automáticamente.
        const cs = (meta as { currentStep?: string | null }).currentStep;
        if (
          meta.pipelineStatus === "analyzing" &&
          cs &&
          !analyzeChainActive.current
        ) {
          void chainAnalyzeStep(id);
        }
      } catch {
        // Silencioso — próximo poll reintentará.
      }
    }, 10_000);
  };

  // Fase 49 — Dispara el análisis estructural P3 (step-by-step).
  const handleAnalyze = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();

    setAnalyzeLoadingId(id);
    setPipelineError(null);
    setStructureDoc(null);
    setStructureLoading(false);
    setStructureError(null);

    try {
      await runPipelineAnalyze(id);
    } catch (err) {
      if (
        err instanceof ApiError &&
        err.status === 409 &&
        (err.body as { currentPipelineStatus?: string } | null)
          ?.currentPipelineStatus === "analyzing"
      ) {
        // Ya está en "analyzing". Reanudar chain + polling.
        startAnalyzePolling(id);
        void chainAnalyzeStep(id);
        return;
      }
      const msg =
        err instanceof ApiError
          ? err.message
          : "No se pudo iniciar el análisis estructural.";
      setPipelineError(msg);
      setAnalyzeLoadingId(null);
      return;
    }

    // Iniciar polling y chain de pasos.
    startAnalyzePolling(id);
    void chainAnalyzeStep(id);
  };

  // Fase 49 — Abre la estructura ya generada.
  const handleOpenStructure = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setStructureDoc(null);
    setStructureLoading(false);
    setStructureError(null);
    navigateTo.structure(id);
  };

  // Fase 55 — Abre el informe en ReportScreen.
  const handleOpenReport = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    navigateTo.report(id);
  };

  // Fase 57 — Abre la revisión final (informe + drafts).
  const handleOpenReviewReport = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    navigateTo.reviewReport(id);
  };

  // Fase 53 — Dispara la generación del informe IA (P5).
  const handleReport = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setReportLoadingId(id);
    try {
      await runPipelineReport(id);
      await fetchProcessings();
    } catch (err) {
      console.error("[MeetingsScreen] Error generando informe:", err);
    } finally {
      setReportLoadingId(null);
    }
  };

  // Fase 56 — Dispara la generación de borradores de email (P6).
  const handleRunDrafts = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setDraftsLoadingId(id);
    try {
      await runPipelineDrafts(id);
      await fetchProcessings();
    } catch (err) {
      console.error("[MeetingsScreen] Error generando drafts:", err);
    } finally {
      setDraftsLoadingId(null);
    }
  };

  useEffect(() => {
    return () => {
      if (pipelinePollRef.current) clearInterval(pipelinePollRef.current);
      if (analyzePollRef.current) clearInterval(analyzePollRef.current);
    };
  }, []);

  return (
    <Container>
      <Header>
        <Title>Reuniones</Title>
        <HeaderActions>
          <Button $variant="ghost" onClick={handleAdmin}>
            ⚙ Admin
          </Button>
          <Button $variant="primary" onClick={handleNewMeeting}>
            + Nueva reunión
          </Button>
        </HeaderActions>
      </Header>

      {deleteError && (
        <DeleteErrorBanner>
          {deleteError}
          <Button $variant="ghost" onClick={() => setDeleteError(null)}>✕</Button>
        </DeleteErrorBanner>
      )}

      {redownloadError && (
        <DeleteErrorBanner>
          {redownloadError}
          <Button $variant="ghost" onClick={() => setRedownloadError(null)}>✕</Button>
        </DeleteErrorBanner>
      )}

      {pipelineError && (
        <DeleteErrorBanner>
          {pipelineError}
          <Button $variant="ghost" onClick={() => setPipelineError(null)}>✕</Button>
        </DeleteErrorBanner>
      )}

      {loading && <Status>Cargando…</Status>}

      {error && !loading && (
        <ErrorBox>
          <p>{error}</p>
          <Button $variant="secondary" onClick={() => void fetchProcessings()}>
            Reintentar
          </Button>
        </ErrorBox>
      )}

      {!loading && !error && list && list.length === 0 && (
        <EmptyState>
          <p>Todavía no procesaste ninguna reunión.</p>
          <p>
            Tocá <strong>+ Nueva reunión</strong> para subir un audio y que se
            transcriba automáticamente.
          </p>
        </EmptyState>
      )}

      {!loading && !error && list && list.length > 0 && (
        <Stack $direction="column" $gap="sm">
          {list.map((p) => {
            const isConfirming = confirmingId === p.id;
            const isDeleting = deletingId === p.id;
            const isRedownloading = redownloadingId === p.id;
            const isPipelineRunning = pipelineRunningId === p.id;
            const showTaggedActions = hasTaggedSrt(p);
            const showCorrectionReport = hasCorrectionReport(p);
            const showConfirmed = hasConfirmedSrt(p);
            const showPipelineTrigger = showTaggedActions && !showCorrectionReport;
            return (
              <ProcessingItem
                key={p.id}
                onClick={() => handleOpenMeeting(p)}
                role="button"
                tabIndex={0}
                $faded={isDeleting}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleOpenMeeting(p);
                  }
                }}
              >
                <ItemHeader>
                  <ItemTitle>
                    {p.id}
                    {isDone(p) && <DoneBadge>✓ Procesada</DoneBadge>}
                  </ItemTitle>
                </ItemHeader>
                <ItemActions onClick={(e) => e.stopPropagation()}>
                  {isDeleting ? (
                    <DeletingLabel>Borrando…</DeletingLabel>
                  ) : isConfirming ? (
                    <ConfirmRow>
                      <ConfirmLabel>¿Borrar?</ConfirmLabel>
                      <Button
                        $variant="primary"
                        onClick={(e) => void handleDeleteConfirm(e, p.id)}
                      >
                        Confirmar
                      </Button>
                      <Button $variant="ghost" onClick={handleDeleteCancel}>
                        Cancelar
                      </Button>
                    </ConfirmRow>
                  ) : (
                    <ActionButtonsRow>
                      {showConfirmed && (
                        <>
                          <Button
                            $variant="ghost"
                            onClick={(e) => handleViewConfirmedSrt(e, p.id)}
                          >
                            👁 Ver corregido
                          </Button>
                          {hasStructure(p) ? (
                            <>
                              <Button
                                $variant="primary"
                                onClick={(e) => handleOpenStructure(e, p.id)}
                              >
                                👁 Ver estructura
                              </Button>
                              {hasReport(p) ? (
                                <>
                                  <Button
                                    $variant="primary"
                                    onClick={(e) => handleOpenReport(e, p.id)}
                                  >
                                    📄 Ver informe
                                  </Button>
                                  {hasDrafts(p) ? (
                                    <Button
                                      $variant="primary"
                                      onClick={(e) => handleOpenReviewReport(e, p.id)}
                                    >
                                      📧 Revisar drafts
                                    </Button>
                                  ) : (
                                    <Button
                                      $variant="primary"
                                      onClick={(e) => void handleRunDrafts(e, p.id)}
                                      disabled={draftsLoadingId === p.id}
                                    >
                                      {draftsLoadingId === p.id
                                        ? "⏳ Generando drafts…"
                                        : "📧 Generar drafts (P6)"}
                                    </Button>
                                  )}
                                </>
                              ) : (
                                <Button
                                  $variant="primary"
                                  onClick={(e) => void handleReport(e, p.id)}
                                  disabled={reportLoadingId === p.id}
                                >
                                  {reportLoadingId === p.id
                                    ? "⏳ Generando informe…"
                                    : "📄 Generar informe (P5)"}
                                </Button>
                              )}
                            </>
                          ) : (
                            <Button
                              $variant="primary"
                              onClick={(e) => void handleAnalyze(e, p.id)}
                              disabled={analyzeLoadingId === p.id}
                            >
                              {analyzeLoadingId === p.id
                                ? "⏳ Analizando estructura…"
                                : "🔍 Analizar estructura (P3)"}
                            </Button>
                          )}
                        </>
                      )}
                      {!showConfirmed && showCorrectionReport && (
                        <Button
                          $variant="primary"
                          onClick={(e) => handleOpenCorrections(e, p.id)}
                        >
                          ✏️ Validar correcciones
                        </Button>
                      )}
                      {showPipelineTrigger && (
                        <Button
                          $variant="primary"
                          onClick={(e) => void handleRunPipeline(e, p.id)}
                          disabled={isPipelineRunning}
                        >
                          {isPipelineRunning ? "⏳ Analizando…" : "🤖 Iniciar análisis IA"}
                        </Button>
                      )}
                      {showTaggedActions && (
                        <>
                          <Button
                            $variant="ghost"
                            onClick={(e) => handleViewTaggedSrt(e, p.id)}
                          >
                            👁 Ver
                          </Button>
                          <Button
                            $variant="ghost"
                            onClick={(e) => void handleRedownload(e, p.id)}
                            disabled={isRedownloading}
                          >
                            {isRedownloading ? "Descargando…" : "↓ Descargar"}
                          </Button>
                        </>
                      )}
                      <Button
                        $variant="ghost"
                        onClick={(e) => handleDeleteClick(e, p.id)}
                      >
                        Borrar
                      </Button>
                    </ActionButtonsRow>
                  )}
                </ItemActions>
                <FileList>
                  {p.files.map((f) => (
                    <FileRow key={f.name}>
                      <span>{f.name}</span>
                      {typeof f.size === "number" && <span>{formatSize(f.size)}</span>}
                    </FileRow>
                  ))}
                </FileList>
                {!hasFinalOutputs(p) && (
                  <Hint>
                    {hasAudio(p) && !hasTranscript(p)
                      ? "Transcripción en progreso o pendiente. Tocá para continuar."
                      : "Procesamiento incompleto."}
                  </Hint>
                )}
              </ProcessingItem>
            );
          })}
        </Stack>
      )}
    </Container>
  );
}

function hasAudio(p: ProcessingSummary): boolean {
  return p.files.some((f) => f.name === "audio.wav");
}

function hasTranscript(p: ProcessingSummary): boolean {
  return p.files.some((f) => f.name === "transcript.json");
}

function hasFinalOutputs(p: ProcessingSummary): boolean {
  const names = new Set(p.files.map((f) => f.name));
  return names.has("transcript.json") && names.has("subtitles.srt");
}

// Detecta si el procesamiento ya fue exportado al bucket.
// Match exacto con el filename canónico que escribe el handler de export.
function hasTaggedSrt(p: ProcessingSummary): boolean {
  return p.files.some((f) => f.name === "tagged.srt");
}

// Fase 38 — Detecta si el pipeline P1 ya generó el reporte de correcciones.
function hasCorrectionReport(p: ProcessingSummary): boolean {
  return p.files.some((f) => f.name === "correction-report.json");
}

// Fase 43 — Detecta si el SRT confirmado (post-validación humana) existe.
function hasConfirmedSrt(p: ProcessingSummary): boolean {
  return p.files.some((f) => f.name === "srt-confirmed.srt");
}

// Fase 49 — Detecta si structure.json ya existe (P3 ya corrió).
function hasStructure(p: ProcessingSummary): boolean {
  return p.files.some((f) => f.name === "structure.json");
}

// Fase 53 — Detecta si report.md ya existe (P5 ya corrió).
function hasReport(p: ProcessingSummary): boolean {
  return p.files.some((f) => f.name === "report.md");
}

// Fase 56 — Detecta si email-drafts.json ya existe (P6 ya corrió).
function hasDrafts(p: ProcessingSummary): boolean {
  return p.files.some((f) => f.name === "email-drafts.json");
}

// Fase 60 — Detecta si el pipeline está cerrado (audit.json existe).
function hasAudit(p: ProcessingSummary): boolean {
  return p.files.some((f) => f.name === "audit.json");
}

// Fase 60 — Detecta si la reunión está completamente procesada.
function isDone(p: ProcessingSummary): boolean {
  return hasAudit(p) && hasReport(p) && hasDrafts(p);
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const Container = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.space.lg};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.space.lg};
`;

// Fase 32 — agrupa los botones del header. "⚙ Admin" como ghost a la
// izquierda, "+ Nueva reunión" como primario a la derecha. El primario
// queda más cerca del borde derecho preservando la afordancia del CTA.
const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.font.size.xxl};
  margin: 0;
`;

const Status = styled.div`
  padding: ${({ theme }) => theme.space.md};
  text-align: center;
  color: ${({ theme }) => theme.color.textMuted};
`;

const ErrorBox = styled(Card)`
  border-color: ${({ theme }) => theme.color.error};
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.space.md};
`;

const DeleteErrorBanner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => `${theme.space.sm} ${theme.space.md}`};
  margin-bottom: ${({ theme }) => theme.space.sm};
  background: ${({ theme }) => theme.color.errorMuted};
  color: ${({ theme }) => theme.color.error};
  border: 1px solid ${({ theme }) => theme.color.error};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.font.size.sm};
`;

const EmptyState = styled(Card)`
  text-align: center;
  padding: ${({ theme }) => theme.space.xl};
  color: ${({ theme }) => theme.color.textMuted};
`;

const ProcessingItem = styled(Card)<{ $faded?: boolean }>`
  padding: ${({ theme }) => theme.space.md};
  cursor: pointer;
  transition: border-color 0.15s ease, transform 0.05s ease, opacity 0.15s ease;
  opacity: ${({ $faded }) => ($faded ? 0.5 : 1)};
  pointer-events: ${({ $faded }) => ($faded ? "none" : "auto")};

  &:hover {
    border-color: ${({ theme }) => theme.color.accent};
  }

  &:active {
    transform: translateY(1px);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.accent};
    outline-offset: 2px;
  }
`;

const ItemHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.space.sm};
`;

const ItemTitle = styled.h3`
  font-size: ${({ theme }) => theme.font.size.lg};
  margin: 0;
  font-family: ${({ theme }) => theme.font.mono};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DoneBadge = styled.span`
  font-size: ${({ theme }) => theme.font.size.xs};
  font-weight: ${({ theme }) => theme.font.weight.bold};
  color: ${({ theme }) => theme.color.success};
  background: ${({ theme }) => theme.color.successMuted};
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  margin-left: ${({ theme }) => theme.space.sm};
  vertical-align: middle;
  font-family: ${({ theme }) => theme.font.family};
`;

const ItemActions = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.space.sm};
`;

const ActionButtonsRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.xs};
  flex-wrap: wrap;
`;

const ConfirmRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.xs};
`;

const ConfirmLabel = styled.span`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.error};
  font-weight: ${({ theme }) => theme.font.weight.bold};
  white-space: nowrap;
`;

const DeletingLabel = styled.span`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.textMuted};
  font-style: italic;
`;

const FileList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FileRow = styled.li`
  display: flex;
  justify-content: space-between;
  font-family: ${({ theme }) => theme.font.mono};
  font-size: ${({ theme }) => theme.font.size.sm};
  padding: ${({ theme }) => `${theme.space.xs} 0`};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Hint = styled.div`
  margin-top: ${({ theme }) => theme.space.sm};
  padding-top: ${({ theme }) => theme.space.sm};
  border-top: 1px solid ${({ theme }) => theme.color.border};
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.textMuted};
  font-style: italic;
`;
